import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, Store, PlusCircle, ArrowLeft, Share2, 
  Navigation, 
  ExternalLink, Search, Check, Copy, Building2, Vote, ThumbsUp
} from 'lucide-react';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import BusinessCard from '../components/BusinessCard';
import VillageSurveyCard from '../components/VillageSurveyCard';
import api from '../utils/api';
import { useSubmitBusiness } from '../contexts/SubmitBusinessContext';
import { showSuccess } from '../utils/toast';
import { isUserLoggedIn } from '../utils/auth';

const getPhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
  const backendBase = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:5000'
    : (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : '');
  return `${backendBase}${photo.startsWith('/') ? photo : `/${photo}`}`;
};


export default function VillageDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [copied, setCopied] = useState(false);
  const [topCandidates, setTopCandidates] = useState([]);
  const { openModal } = useSubmitBusiness();

  useEffect(() => {
    const fetchVillageData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/villages/${slug}/businesses`);
        setData(res.data);
      } catch (err) {
        console.error('Error fetching village details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVillageData();

    // Fetch top voted Bhavi Sarpanch candidates for this village
    api.get(`/bhavi-sarpanch?villageSlug=${slug}`)
      .then((res) => {
        if (res.data?.success) {
          setTopCandidates((res.data.data || []).slice(0, 3));
        }
      })
      .catch((err) => console.error('Error fetching top candidates:', err));
  }, [slug]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    api.get('/favourites').then((r) => {
      const list = r.data?.favourites || [];
      setFavouriteIds(new Set(list.map((b) => (b._id || b).toString())));
    }).catch(() => {});
  }, []);

  const { village, businesses = [], nearbyVillages = [] } = data || {};

  // Extract categories present in this village's businesses
  const categoriesList = useMemo(() => {
    if (!businesses.length) return ['All'];
    const catMap = new Map();
    businesses.forEach(b => {
      const catName = b.category?.name || 'Other';
      catMap.set(catName, (catMap.get(catName) || 0) + 1);
    });

    const list = [{ name: 'All', count: businesses.length }];
    catMap.forEach((count, name) => {
      list.push({ name, count });
    });
    return list;
  }, [businesses]);

  // Filtered businesses based on selected category & search query
  const filteredBusinesses = useMemo(() => {
    return businesses.filter(b => {
      const matchesCat = selectedCategory === 'All' || (b.category?.name === selectedCategory);
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || (
        b.name?.toLowerCase().includes(q) ||
        b.description?.toLowerCase().includes(q) ||
        b.address?.toLowerCase().includes(q) ||
        b.phone?.includes(q)
      );
      return matchesCat && matchesSearch;
    });
  }, [businesses, selectedCategory, searchQuery]);

  // WhatsApp Share handler
  const handleWhatsAppShare = () => {
    if (!village) return;
    const text = encodeURIComponent(
      `🏡 *गाँव ${village.name} (${village.block} ब्लॉक, फतेहाबाद)* की सभी दुकानों, अटल सेवा केंद्र, स्कूल, अस्पताल व सेवाओं की लिस्ट देखें:\n${window.location.href}\n\n_Digital Fatehabad - आपका डिजिटल जिला_`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Copy Link handler
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    showSuccess('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) {
    return <PageLoader message="Loading village directory..." />;
  }

  if (!data || !village) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center p-6 text-center">
        <MapPin className="w-16 h-16 text-gray-300 dark:text-gray-700 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Village Not Found</h2>
        <p className="text-gray-500 mb-6">The village you are looking for does not exist in our directory.</p>
        <Link to="/villages" className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700">
          Browse All Villages
        </Link>
      </div>
    );
  }

  // Google Maps Query
  const mapSearchQuery = encodeURIComponent(`${village.name} village, ${village.block}, Fatehabad, Haryana ${village.pincode}`);
  const googleMapsDirectionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapSearchQuery}`;

  return (
    <>
      <SEO
        title={`${village.name} Village (${village.block} Block) | Shops, Services, Map & Directory in ${village.name}`}
        description={`Explore verified shops, Atal Seva Kendra, schools, clinics, map location, and businesses in ${village.name} village, ${village.block} Block, Fatehabad district, Haryana (${village.pincode}).`}
        canonical={`/village/${village.slug}`}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-20">
        {/* HERO BANNER - Sleek Deep Dark Theme */}
        <div className="relative bg-gray-950 text-white py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-gray-800/80 shadow-2xl overflow-hidden">
          {/* Subtle dark gradient overlay & ambient glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-950 to-gray-950 pointer-events-none" />
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none" />
          <div className="absolute -top-24 right-0 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-7xl mx-auto relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-400 mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="text-gray-600">/</span>
              <Link to="/villages" className="hover:text-white transition-colors">Villages</Link>
              <span className="text-gray-600">/</span>
              <span className="text-gray-200 font-semibold">{village.name} ({village.block} Block)</span>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-950/70 text-emerald-300 border border-emerald-800/60 text-xs font-semibold backdrop-blur-md">
                    <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                    {village.block} Block
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-900/90 text-gray-300 border border-gray-800 text-xs font-semibold backdrop-blur-md">
                    PIN: {village.pincode}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-900/90 text-gray-300 border border-gray-800 text-xs font-semibold backdrop-blur-md">
                    District Fatehabad
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-950/70 text-amber-300 border border-amber-800/60 text-xs font-semibold backdrop-blur-md">
                    {businesses.length} Listed Services
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-2.5">
                  गाँव {village.name}
                  <span className="text-xl sm:text-2xl font-normal text-gray-400 ml-3">
                    ({village.name} Village)
                  </span>
                </h1>
                <p className="text-gray-300 text-sm sm:text-base max-w-2xl font-normal leading-relaxed">
                  फतेहाबाद जिले की ग्राम पंचायत {village.name} के स्थानीय व्यापार, अटल सेवा केंद्र (CSC), स्कूल, स्वास्थ्य केंद्र एवं आवश्यक संपर्क सूत्र।
                </p>
              </div>

              {/* Action Buttons: Add Shop + Share */}
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={() => openModal({ village: village._id })}
                  className="flex-1 sm:flex-none px-3 sm:px-5 py-2.5 sm:py-3 bg-white text-gray-950 hover:bg-gray-100 rounded-xl font-bold shadow-lg shadow-black/30 transition-all active:scale-95 flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap"
                >
                  <PlusCircle className="w-4 h-4 text-primary-600 flex-shrink-0" />
                  <span>+ Add Shop<span className="hidden sm:inline"> in {village.name}</span></span>
                </button>

                {/* WhatsApp Viral Share Button */}
                <button
                  onClick={handleWhatsAppShare}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2.5 sm:py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg shadow-emerald-950/40 transition-all active:scale-95 flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap"
                  title="Share village directory on WhatsApp"
                >
                  <Share2 className="w-4 h-4 flex-shrink-0" />
                  <span>WhatsApp शेयर<span className="hidden sm:inline"> करें</span></span>
                </button>

                {/* Copy Link Button */}
                <button
                  onClick={handleCopyLink}
                  className="p-2.5 sm:p-3 bg-gray-900/90 hover:bg-gray-800 border border-gray-800 text-gray-300 hover:text-white rounded-xl transition-all active:scale-95 flex-shrink-0"
                  title="Copy Page Link"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* QUICK INFO & WIDGETS */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* 1. Village Profile Summary Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 mb-2">
                  <Building2 className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase tracking-wider">Village Profile</span>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base">ग्राम पंचायत {village.name}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Block: <strong className="text-gray-800 dark:text-gray-200">{village.block}</strong> &bull; PIN: <strong className="text-gray-800 dark:text-gray-200">{village.pincode}</strong>
                </p>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500">
                <span>District: Fatehabad</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">Haryana</span>
              </div>
            </div>

            {/* 2. Sarpanch Satisfaction Review Card (Compact) */}
            <VillageSurveyCard village={village} compact={true} />

            {/* 4. Bhavi Sarpanch Candidates Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 shadow-md flex flex-col justify-between group hover:border-amber-400 dark:hover:border-amber-600 transition-all">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                    <Vote className="w-5 h-5" />
                    <span className="text-xs font-bold uppercase tracking-wider">भावी सरपंच</span>
                  </div>
                  {topCandidates.length > 0 && (
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                      Top {topCandidates.length} प्रत्याशी
                    </span>
                  )}
                </div>

                <h3 className="font-bold text-gray-900 dark:text-white text-base">भावी सरपंच प्रत्याशी</h3>

                {/* Top 3 Voted Candidates Avatars & Info */}
                {topCandidates.length > 0 ? (
                  <div className="mt-2.5">
                    <div className="flex items-center gap-2.5">
                      {/* Overlapping Candidate Avatar Icons with Rank Badges */}
                      <div className="flex -space-x-2 overflow-hidden py-1 shrink-0">
                        {topCandidates.map((cand, idx) => (
                          <Link
                            key={cand._id}
                            to={`/bhavi-sarpanch?village=${village.slug}`}
                            className="relative inline-block ring-2 ring-white dark:ring-gray-900 rounded-full transition-transform hover:scale-110 hover:z-10"
                            title={`${cand.name} (${cand.votesCount || 0} वोट)`}
                          >
                            {cand.photo ? (
                              <img
                                src={getPhotoUrl(cand.photo)}
                                alt={cand.name}
                                className="w-9 h-9 rounded-full object-cover bg-amber-100"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(cand.name) + '&background=f59e0b&color=fff&size=80';
                                }}
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-amber-500 to-amber-600 text-white font-black text-xs flex items-center justify-center">
                                {cand.name.charAt(0)}
                              </div>
                            )}
                            {/* Rank Badge */}
                            <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-white border border-white dark:border-gray-900 ${
                              idx === 0 ? 'bg-amber-500 shadow-sm' : idx === 1 ? 'bg-slate-500 shadow-sm' : 'bg-amber-700 shadow-sm'
                            }`}>
                              #{idx + 1}
                            </span>
                          </Link>
                        ))}
                      </div>

                      {/* Candidate Names & Votes Preview */}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-gray-900 dark:text-white truncate">
                          {topCandidates[0]?.name}
                          {topCandidates.length > 1 && (
                            <span className="text-[11px] font-normal text-gray-400 ml-1">
                              +{topCandidates.length - 1} अन्य
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-purple-600 dark:text-purple-400 font-bold flex items-center gap-1 mt-0.5">
                          <ThumbsUp className="w-3 h-3" />
                          <span>{topCandidates[0]?.votesCount || 0} वोट (Top Voted)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                    गाँव {village.name} के भावी प्रत्याशियों का विज़न देखें व जनसमर्थन (वोट) दें।
                  </p>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs font-bold">
                <Link
                  to={`/bhavi-sarpanch?village=${village.slug}`}
                  className="text-amber-600 dark:text-amber-400 flex items-center gap-1 group-hover:underline"
                >
                  प्रत्याशी देखें व वोट करें <ExternalLink className="w-3 h-3" />
                </Link>
                <Link
                  to={`/bhavi-sarpanch?village=${village.slug}`}
                  className="text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 font-semibold"
                >
                  + आवेदन
                </Link>
              </div>
            </div>

          </div>
        </div>

        {/* LISTINGS & CATEGORY FILTER SECTION */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                <Store className="w-6 h-6 text-primary-600" />
                दुकानें एवं सेवाएं (Listings in {village.name})
                <span className="text-base font-normal text-gray-400 ml-2">
                  ({filteredBusinesses.length} {filteredBusinesses.length === 1 ? 'Service' : 'Services'})
                </span>
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                दुकानों के फोन नंबर, पता और सेवाएं सीधे देखें।
              </p>
            </div>

            {/* Search Input within this Village */}
            <div className="w-full md:w-72 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search in ${village.name}...`}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
              />
            </div>
          </div>

          {/* Category Filter Tabs */}
          {categoriesList.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 no-scrollbar">
              {categoriesList.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all shrink-0 flex items-center gap-1.5 ${
                    selectedCategory === cat.name
                      ? 'bg-primary-600 text-white shadow-md shadow-primary-600/25'
                      : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span>{cat.name}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    selectedCategory === cat.name
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* 2-Column Responsive Cards Grid */}
          {filteredBusinesses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-0 md:gap-6 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800">
              {filteredBusinesses.map((business) => (
                <BusinessCard
                  key={business._id}
                  business={business}
                  favouriteIds={favouriteIds}
                  onAddFavourite={(id) => setFavouriteIds((prev) => new Set(prev).add(id))}
                  onRemoveFavourite={(id) => {
                    const next = new Set(favouriteIds);
                    next.delete(id);
                    setFavouriteIds(next);
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-10 md:p-14 text-center border border-gray-200 dark:border-gray-800 shadow-sm max-w-xl mx-auto">
              <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400">
                <Search className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                No matching services found
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-5">
                {searchQuery 
                  ? `No listings matched "${searchQuery}" in ${selectedCategory} category.`
                  : `No listings available in ${selectedCategory} category in ${village.name}.`}
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-800 dark:text-gray-200 rounded-xl text-xs font-semibold"
                >
                  Reset Filters
                </button>
                <button
                  onClick={() => openModal({ village: village._id })}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold"
                >
                  + Add This Service
                </button>
              </div>
            </div>
          )}
        </div>

        {/* NEARBY VILLAGES IN THE SAME BLOCK */}
        {nearbyVillages.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    Nearby Villages in {village.block} Block (आसपास के गांव)
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Explore other villages and Gram Panchayats in {village.block} Block.
                  </p>
                </div>
                <Link
                  to="/villages"
                  className="text-xs font-bold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                >
                  All 274 Villages <ArrowLeft className="w-3 h-3 rotate-180" />
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {nearbyVillages.map((nv) => (
                  <Link
                    key={nv._id}
                    to={`/village/${nv.slug}`}
                    className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-white dark:hover:bg-gray-800 hover:shadow-sm transition-all text-center flex flex-col justify-center group"
                  >
                    <span className="font-semibold text-xs text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 truncate">
                      {nv.name}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                      PIN: {nv.pincode}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* INTERACTIVE GOOGLE MAP SECTION - Moved to Bottom */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-800 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-primary-600" />
                  गाँव {village.name} का लाइव मैप (Interactive Map Location)
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Explore village roads, main chowk, bus stop, and nearby boundaries on Google Maps.
                </p>
              </div>
              <a
                href={googleMapsDirectionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-950/50 hover:bg-primary-100 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 rounded-xl text-xs font-bold transition-colors shrink-0"
              >
                <Navigation className="w-3.5 h-3.5" />
                Google Maps पर रास्ता देखें (Get Directions)
              </a>
            </div>

            {/* Embedded Responsive Google Map Iframe */}
            <div className="w-full h-64 sm:h-80 md:h-96 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner bg-gray-100 dark:bg-gray-800 relative">
              <iframe
                title={`Map of ${village.name} Village, Fatehabad`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${mapSearchQuery}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
              />
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
